import { useEffect, useMemo, useState } from 'react';
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import { useInvalidate, type CrudFilters } from '@refinedev/core';
import { Link } from 'react-router';
import { Eye, Mail, Pencil, UserPlus } from 'lucide-react';
import type { IdentityConfig, TfUserRow } from '@verybigthings/trackframe-shared/schemas';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/DataTable';
import { TableSearch } from '@/components/TableSearch';
import { fmtDate } from '@/lib/format';
import { filterOperator, filterValue } from '@/lib/list-filters';
import { cn } from '@/lib/utils';
import { hub } from '@/lib/hub-client';
import { InviteUserDialog, canResendInvite, inviteEnabled, sendInvite } from './InviteUserDialog';

/** `onResend` is absent while email sending is off — no resend button then (HUB-106). */
function buildColumns(onResend?: (user: TfUserRow) => void): ColumnDef<TfUserRow>[] {
  return [
  {
    id: 'email',
    header: 'User',
    accessorKey: 'email',
    cell: (c) => {
      const u = c.row.original;
      return (
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold uppercase text-accent-foreground">
            {(u.name || u.email).trim().slice(0, 1)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium">{u.email}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {u.name ?? 'No display name'}
            </span>
          </span>
        </div>
      );
    },
  },
  {
    id: 'roles',
    header: 'Roles',
    accessorKey: 'roles',
    cell: (c) => {
      const roles = c.getValue() as string[];
      if (roles.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {roles.map((r) => (
            <Badge key={r} variant={r === 'admin' ? 'accent' : 'secondary'}>{r}</Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'disabledAt',
    cell: (c) =>
      c.getValue() ? (
        <Badge variant="muted" className="rounded-full">
          <span className="size-1.5 rounded-full bg-muted-foreground" aria-hidden="true" />
          Disabled
        </Badge>
      ) : (
        <Badge variant="success" className="rounded-full">
          <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
          Active
        </Badge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    accessorKey: 'createdAt',
    cell: (c) => <span className="tabular-nums text-muted-foreground">{fmtDate(c.getValue() as string)}</span>,
  },
  {
    id: 'actions',
    header: '',
    cell: (c) => (
      <div className="flex justify-end gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none">
        {onResend && canResendInvite(c.row.original) && (
          <button
            type="button"
            onClick={() => onResend(c.row.original)}
            className={cn(buttonVariants({ size: 'icon', variant: 'ghost' }), 'size-8')}
          >
            <Mail className="size-4" aria-hidden="true" />
            <span className="sr-only">Resend invite</span>
          </button>
        )}
        <Link
          to={`/admin/users/show/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'icon', variant: 'ghost' }), 'size-8')}
        >
          <Eye className="size-4" aria-hidden="true" />
          <span className="sr-only">View</span>
        </Link>
        <Link
          to={`/admin/users/edit/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'icon', variant: 'ghost' }), 'size-8')}
        >
          <Pencil className="size-4" aria-hidden="true" />
          <span className="sr-only">Edit</span>
        </Link>
      </div>
    ),
  },
  ];
}

const ROLE_OPTIONS = ['all', 'admin', 'member'] as const;
const STATUS_OPTIONS = ['all', 'active', 'disabled'] as const;

export interface UsersFilterState {
  search: string;
  role: string;
  status: string;
}

/**
 * Toolbar state ⇄ refine filters. Both directions are needed: the URL is the
 * only place the toolbar survives a reload (list-filters.test).
 */
export function usersCrudFilters({ search, role, status }: UsersFilterState): CrudFilters {
  const filters: CrudFilters = [];
  const q = search.trim();
  if (q) filters.push({ field: 'q', operator: 'contains', value: q });
  if (role !== 'all') filters.push({ field: 'roles', operator: 'contains', value: role });
  // `value` MUST be non-null. refine's `unionFilters` drops null-valued
  // filters outright, so the Status chip used to look active and change
  // nothing. `null`/`nnull` ignore the value — `true` just keeps it alive.
  if (status !== 'all') {
    filters.push({
      field: 'disabledAt',
      operator: status === 'active' ? 'null' : 'nnull',
      value: true,
    });
  }
  return filters;
}

export function usersFilterState(filters: CrudFilters | undefined): UsersFilterState {
  const disabledAt = filterOperator(filters, 'disabledAt');
  return {
    search: filterValue(filters, 'q'),
    role: filterValue(filters, 'roles') || 'all',
    status: disabledAt === 'null' ? 'active' : disabledAt === 'nnull' ? 'disabled' : 'all',
  };
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  // Click cycles through the options — All → each value → All.
  const next = options[(options.indexOf(value) + 1) % options.length];
  return (
    <button
      type="button"
      onClick={() => onChange(next)}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1 rounded-full border border-dashed px-3 py-1.5 text-xs font-medium capitalize transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        value === 'all' ? 'text-muted-foreground' : 'border-solid border-ring bg-accent text-accent-foreground',
      )}
    >
      {label}: {value}
    </button>
  );
}

export function UsersList() {
  const invalidate = useInvalidate();
  const [identityConfig, setIdentityConfig] = useState<IdentityConfig | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const canInvite = inviteEnabled(identityConfig);

  useEffect(() => {
    let cancelled = false;
    hub.identityConfigGet()
      .then((cfg) => { if (!cancelled) setIdentityConfig(cfg); })
      .catch(() => { /* invite stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  const refreshList = () => invalidate({ resource: 'users', invalidates: ['list'] });

  const resend = async (user: TfUserRow) => {
    setNotice(null);
    try {
      await sendInvite(user.id);
      setNotice(`Invite sent to ${user.email}.`);
    } catch (err) {
      setNotice((err as Error).message ?? 'Resend failed');
    }
  };

  const columns = useMemo(() => buildColumns(canInvite ? resend : undefined), [canInvite]);
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<TfUserRow>({ columns });
  // Seeded from the filters refine restored from the URL — otherwise the
  // effect below fires on mount with empty values and erases them.
  const [state, setState] = useState<UsersFilterState>(() => usersFilterState(filters));
  const { search, role, status } = state;

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(usersCrudFilters(state), 'replace');
    }, 250);
    return () => clearTimeout(handle);
  }, [state, setFilters]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">Hub identities and roles.</p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="size-4" aria-hidden="true" />
            Invite user
          </Button>
        )}
      </div>
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(email) => {
          setNotice(`Invite sent to ${email}.`);
          refreshList();
        }}
      />
      <DataTable
        table={table}
        toolbar={
          <>
            <TableSearch
              value={search}
              onChange={(next) => setState((s) => ({ ...s, search: next }))}
              placeholder="Filter by email or name…"
            />
            <FilterChip
              label="Role"
              value={role}
              options={ROLE_OPTIONS}
              onChange={(next) => setState((s) => ({ ...s, role: next }))}
            />
            <FilterChip
              label="Status"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(next) => setState((s) => ({ ...s, status: next }))}
            />
          </>
        }
      />
    </div>
  );
}
